/**
 * 資料エディタ — セクション追加ダイアログ。
 * registry を引いて、用途別タブで section type を絞り込み + variant を選んで追加。
 * テンプレタブから保存済みセクションテンプレを挿入することも可能。
 */
import type {
  DocumentSection,
  DocumentSectionType,
  DocumentUseCase,
} from '@goenhub/shared';
import { Bookmark, Loader2, Trash2 } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from '@/components/ui/toast';
import {
  useDeleteDocumentSectionTemplate,
  useDocumentSectionTemplates,
  type DocumentSectionTemplateRecord,
} from '@/hooks/useDocumentSectionTemplates';
import {
  useUpsertDocument,
  type DocumentRecord,
} from '@/hooks/useDocuments';

import {
  SECTION_REGISTRY,
  SECTION_TYPE_ORDER,
  USE_CASE_LABEL,
  getVariant,
  isSectionForUseCase,
  variantSample,
} from '../sectionRegistry';
import { DocumentSectionRenderer } from './DocumentSectionRenderer';

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  document: DocumentRecord;
  onAdded?: (sectionId: string) => void;
}

type FilterValue = 'all' | DocumentUseCase;

const FILTERS: Array<{ value: FilterValue; label: string }> = [
  { value: 'all', label: 'すべて' },
  { value: 'physical_product', label: USE_CASE_LABEL.physical_product },
  { value: 'service', label: USE_CASE_LABEL.service },
  { value: 'b2b_lease', label: USE_CASE_LABEL.b2b_lease },
  { value: 'platform', label: USE_CASE_LABEL.platform },
];

function genId(): string {
  return `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

type Tab = 'type' | 'template';

export function DocumentAddSectionDialog({
  open,
  onOpenChange,
  document,
  onAdded,
}: Props) {
  const upsert = useUpsertDocument();
  const [tab, setTab] = useState<Tab>('type');
  const [filter, setFilter] = useState<FilterValue>('all');
  const [selectedType, setSelectedType] = useState<DocumentSectionType | null>(
    null,
  );
  const templatesQuery = useDocumentSectionTemplates();
  const deleteTemplate = useDeleteDocumentSectionTemplate();

  const handleClose = () => {
    onOpenChange(false);
    setSelectedType(null);
    setTab('type');
  };

  const handleAddFromTemplate = async (tpl: DocumentSectionTemplateRecord) => {
    const newSection: DocumentSection = {
      id: genId(),
      type: tpl.type,
      variant: tpl.variant ?? undefined,
      config: JSON.parse(JSON.stringify(tpl.config ?? {})),
    };
    try {
      await upsert.mutateAsync({
        id: document.id,
        title: document.title,
        slug: document.slug,
        status: document.status,
        sections: [...(document.sections ?? []), newSection],
        autoReplyTemplate: document.autoReplyTemplate ?? null,
      });
      toast.success(`テンプレ「${tpl.name}」を追加しました`);
      onAdded?.(newSection.id);
      handleClose();
    } catch (e) {
      toast.error(`追加失敗: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  const handleDeleteTemplate = async (tpl: DocumentSectionTemplateRecord) => {
    if (!confirm(`テンプレ「${tpl.name}」を削除しますか？`)) return;
    try {
      await deleteTemplate.mutateAsync(tpl.id);
      toast.success('テンプレを削除しました');
    } catch (e) {
      toast.error(`削除失敗: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  const handleAdd = async (type: DocumentSectionType, variantKey: string) => {
    const def = SECTION_REGISTRY[type];
    if (!def) return;
    const newSection: DocumentSection = {
      id: genId(),
      type,
      variant: variantKey,
      config: variantSample(type, variantKey),
    };
    try {
      await upsert.mutateAsync({
        id: document.id,
        title: document.title,
        slug: document.slug,
        status: document.status,
        sections: [...(document.sections ?? []), newSection],
        autoReplyTemplate: document.autoReplyTemplate ?? null,
      });
      toast.success(`${def.label} (${def.variants[variantKey]?.label ?? variantKey}) を追加しました`);
      onAdded?.(newSection.id);
      handleClose();
    } catch (e) {
      toast.error(`追加失敗: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  const visibleTypes = SECTION_TYPE_ORDER.filter((t) => {
    const def = SECTION_REGISTRY[t];
    if (!def) return false;
    return isSectionForUseCase(def, filter);
  });

  const selectedDef = selectedType ? SECTION_REGISTRY[selectedType] : null;

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) handleClose();
        else onOpenChange(v);
      }}
    >
      <DialogContent className="sm:max-w-[720px] max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>セクションを追加</DialogTitle>
          <DialogDescription>
            {tab === 'template'
              ? '保存済みテンプレートから挿入できます。'
              : selectedDef
                ? `${selectedDef.label} のデザインバリアントを選択してください。`
                : 'タイプを選択してください。用途で絞り込めます。'}
          </DialogDescription>
        </DialogHeader>

        {/* タブ切替: セクションタイプ / テンプレート */}
        <div className="flex gap-1 border-b border-[var(--border)] mb-3">
          <button
            type="button"
            onClick={() => {
              setTab('type');
              setSelectedType(null);
            }}
            className={`px-3 py-1.5 text-xs font-semibold border-b-2 transition-colors ${
              tab === 'type'
                ? 'border-[var(--accent)] text-[var(--accent)]'
                : 'border-transparent text-[var(--text-muted)] hover:text-[var(--text-primary)]'
            }`}
          >
            セクションタイプ
          </button>
          <button
            type="button"
            onClick={() => setTab('template')}
            className={`px-3 py-1.5 text-xs font-semibold border-b-2 transition-colors flex items-center gap-1 ${
              tab === 'template'
                ? 'border-[var(--accent)] text-[var(--accent)]'
                : 'border-transparent text-[var(--text-muted)] hover:text-[var(--text-primary)]'
            }`}
          >
            <Bookmark className="size-3" />
            テンプレート
            {templatesQuery.data && templatesQuery.data.length > 0 && (
              <span className="ml-1 text-[10px] text-[var(--text-muted)]">
                ({templatesQuery.data.length})
              </span>
            )}
          </button>
        </div>

        {tab === 'template' && (
          <TemplateGrid
            templates={templatesQuery.data ?? []}
            isLoading={templatesQuery.isLoading}
            onPick={handleAddFromTemplate}
            onDelete={handleDeleteTemplate}
            disabled={upsert.isPending}
          />
        )}

        {tab === 'type' && !selectedDef && (
          <>
            <div className="flex flex-wrap gap-1 mb-2">
              {FILTERS.map((f) => (
                <button
                  key={f.value}
                  type="button"
                  onClick={() => setFilter(f.value)}
                  className={`px-3 py-1 rounded-full text-[11px] border transition-colors ${
                    filter === f.value
                      ? 'bg-[var(--accent)] text-white border-[var(--accent)]'
                      : 'bg-transparent border-[var(--border)] hover:bg-[var(--bg-hover)]'
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {visibleTypes.map((type) => {
                const def = SECTION_REGISTRY[type]!;
                const Icon = def.icon;
                return (
                  <button
                    key={type}
                    type="button"
                    onClick={() => setSelectedType(type)}
                    disabled={upsert.isPending}
                    className="flex items-start gap-3 p-3 rounded-md border border-[var(--border)] bg-[var(--bg-elevated)] hover:bg-[var(--bg-hover)] text-left transition-colors disabled:opacity-50"
                  >
                    <Icon className="size-5 mt-0.5 text-[var(--accent)]" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold">{def.label}</div>
                      <div className="text-[11px] text-[var(--text-muted)] line-clamp-2">
                        {def.description}
                      </div>
                      <div className="text-[10px] text-[var(--text-muted)] mt-1">
                        {Object.keys(def.variants).length} バリアント
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </>
        )}

        {tab === 'type' && selectedDef && (
          <>
            <button
              type="button"
              onClick={() => setSelectedType(null)}
              className="text-[11px] text-[var(--accent)] hover:underline mb-1 self-start"
            >
              ← タイプ選択に戻る
            </button>
            <div className="grid grid-cols-1 gap-2">
              {Object.entries(selectedDef.variants).map(([key, v]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => handleAdd(selectedDef.type, key)}
                  disabled={upsert.isPending}
                  className="text-left p-3 rounded-md border border-[var(--border)] bg-[var(--bg-elevated)] hover:bg-[var(--bg-hover)] transition-colors disabled:opacity-50"
                >
                  <div className="text-sm font-semibold">{v.label}</div>
                  {v.description && (
                    <div className="text-[11px] text-[var(--text-muted)]">
                      {v.description}
                    </div>
                  )}
                </button>
              ))}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

/**
 * 縮小プレビューラッパ。
 * 親の幅を測定し、`referenceWidth` (デスクトップ幅) に対する
 * 比率で transform: scale() を計算してフィットさせる。
 */
function ScaledPreview({
  children,
  referenceWidth,
}: {
  children: ReactNode;
  referenceWidth: number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(0.35);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => {
      const w = el.clientWidth;
      if (w > 0) setScale(w / referenceWidth);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [referenceWidth]);

  return (
    <div
      ref={ref}
      style={{
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      <div
        style={{
          width: referenceWidth,
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
          pointerEvents: 'none',
          position: 'absolute',
          top: 0,
          left: 0,
        }}
      >
        {children}
      </div>
    </div>
  );
}

/**
 * テンプレート一覧グリッド。各カードはセクションの縮小プレビューを表示。
 * プレビューは `transform: scale(...)` で実コンポーネントを縮小レンダー。
 */
function TemplateGrid({
  templates,
  isLoading,
  onPick,
  onDelete,
  disabled,
}: {
  templates: DocumentSectionTemplateRecord[];
  isLoading: boolean;
  onPick: (tpl: DocumentSectionTemplateRecord) => void;
  onDelete: (tpl: DocumentSectionTemplateRecord) => void;
  disabled: boolean;
}) {
  if (isLoading) {
    return (
      <div className="py-10 flex items-center justify-center text-[11px] text-[var(--text-muted)]">
        <Loader2 className="size-4 animate-spin mr-2" />
        読み込み中...
      </div>
    );
  }
  if (templates.length === 0) {
    return (
      <div className="py-10 text-center text-[11px] text-[var(--text-muted)] border border-dashed border-[var(--border)] rounded-md">
        まだテンプレートがありません。
        <div className="mt-1">
          セクションの操作メニュー内の{' '}
          <Bookmark className="size-3 inline" /> アイコンから保存できます。
        </div>
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[60vh] overflow-y-auto">
      {templates.map((tpl) => (
        <TemplateCard
          key={tpl.id}
          tpl={tpl}
          onPick={() => onPick(tpl)}
          onDelete={() => onDelete(tpl)}
          disabled={disabled}
        />
      ))}
    </div>
  );
}

function TemplateCard({
  tpl,
  onPick,
  onDelete,
  disabled,
}: {
  tpl: DocumentSectionTemplateRecord;
  onPick: () => void;
  onDelete: () => void;
  disabled: boolean;
}) {
  const def = SECTION_REGISTRY[tpl.type];
  const variantInfo = getVariant(tpl.type, tpl.variant);
  const variantLabel = variantInfo?.variant?.label ?? tpl.variant ?? '';

  // プレビュー描画用の section ライク オブジェクト
  const previewSection: DocumentSection = {
    id: tpl.id,
    type: tpl.type,
    variant: tpl.variant ?? undefined,
    config: tpl.config ?? {},
  };

  return (
    <div className="border border-[var(--border)] rounded-md bg-[var(--bg-elevated)] overflow-hidden flex flex-col">
      {/* 縮小プレビュー: カード幅にフィットさせて左右余白を消す */}
      <div
        className="relative bg-[var(--bg-root)] border-b border-[var(--border)] overflow-hidden cursor-pointer"
        style={{ height: 180 }}
        onClick={() => !disabled && onPick()}
      >
        <ScaledPreview referenceWidth={800}>
          <div data-doc-theme="light" style={{ width: 800 }}>
            <div className="p-4">
              <DocumentSectionRenderer section={previewSection} />
            </div>
          </div>
        </ScaledPreview>
        <div className="absolute top-1.5 left-1.5 px-1.5 py-0.5 rounded text-[9px] font-semibold bg-black/60 text-white z-10">
          {def?.label ?? tpl.type}
          {variantLabel && <span className="opacity-70"> · {variantLabel}</span>}
        </div>
      </div>
      {/* メタ + アクション */}
      <div className="p-2.5 flex-1 flex flex-col gap-1">
        <div className="text-xs font-semibold line-clamp-1">{tpl.name}</div>
        {tpl.description && (
          <div className="text-[10px] text-[var(--text-muted)] line-clamp-2">
            {tpl.description}
          </div>
        )}
        <div className="text-[10px] text-[var(--text-muted)] mt-auto">
          {tpl.createdByName ?? '匿名'}
        </div>
        <div className="flex items-center gap-1 mt-1">
          <Button size="sm" onClick={onPick} disabled={disabled} className="flex-1">
            このテンプレを挿入
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            className="text-[var(--text-muted)] hover:text-red-500"
            title="このテンプレを削除"
          >
            <Trash2 className="size-3" />
          </Button>
        </div>
      </div>
    </div>
  );
}
